/**
 * Runtime configuration loader.
 *
 * `appConfig` is the single source of truth for environment-driven values
 * across the app. All configurable values are surfaced as `APP_*` env vars
 * — there is no `STAGE` switch any more. Per-environment differences are
 * managed in `.env` locally and via Helm-generated ConfigMaps / Secrets in
 * kubernetes (see `kube/configmap.example.yaml`).
 *
 * `src/pages/+Head.tsx` reads whitelisted `APP_*` env vars from
 * `process.env` server-side (as part of Vike's Head render, which every
 * serving mode goes through — Vite's own dev middleware, our custom Fastify
 * dev server, and the production Fastify server) and inlines them into the
 * HTML head as `window.__APP_CONFIG__`. This one mechanism works
 * identically in local dev and in deployed environments, so there is no
 * separate `import.meta.env`/`VITE_`-prefixed fallback here — the client
 * only ever reads `window.__APP_CONFIG__`.
 *
 * During SSR there is no `window`, so the server reads the same whitelisted
 * `APP_*` keys straight from `process.env` instead (see `fromServerEnv`).
 * Server and client therefore render with the same values - previously the
 * server fell back to `defaults` (e.g. APP_STAGE 'dev'), which put dev-stage
 * media URLs into production HTML (EM-1183).
 *
 * Add new variables here AND in `src/pages/+Head.tsx` together — the two
 * must stay in sync.
 */
export type AppRuntimeConfig = {
  // Brand & stage (informational — used as path segment for media URLs etc.)
  APP_BRAND: string;
  APP_STAGE: string;

  // API + integrations
  APP_API_BASE_URL: string;
  APP_S3_BASE_URL: string;
  APP_CDN_BASE_URL: string;
  APP_PRE_SIGNED_URL_ENDPOINT: string;
  APP_STRIPE_KEY: string;
  APP_GOOGLE_MAPS_API_KEY: string;
  APP_MUI_X_KEY: string;

  // Google Tag Manager
  APP_GTM_ID: string;
  APP_GTM_AUTH: string;
  APP_GTM_ENV: string;

  // Analytics toggles / keys
  APP_POSTHOG_ENABLED: string;
  APP_POSTHOG_KEY: string;
  APP_KLAVIYO_ENABLED: string;
  APP_KLAVIYO_API_TOKEN: string;
  APP_PLAUSIBLE_ENABLED: string;
  APP_PLAUSIBLE_DATA_DOMAIN: string;

  // Feature flags
  APP_ENABLE_SKIPPER_SELECT: string;
  APP_DISABLE_CALENDAR_SYNC: string;
  APP_FEATURE_READ_RECEIPTS_UI: string;
  APP_FEATURE_MODERATION_ORIGINAL_RESTORE: string;

  // Misc
  APP_OFFER_EXPIRY_THRESHOLD_DAYS: string;
};

declare global {
  interface Window {
    __APP_CONFIG__?: Partial<AppRuntimeConfig>;
  }
}

const fromWindow = (): Partial<AppRuntimeConfig> => {
  if (typeof window === 'undefined') return {};
  return window.__APP_CONFIG__ ?? {};
};

const defaults: AppRuntimeConfig = {
  APP_BRAND: 'ellipsis',
  APP_STAGE: 'dev',

  APP_API_BASE_URL: 'http://localhost:3000/api',
  APP_S3_BASE_URL: 'https://ellipsis-marine-media.s3-accelerate.amazonaws.com',
  APP_CDN_BASE_URL: 'https://cdn.ellipsis.sc',
  APP_PRE_SIGNED_URL_ENDPOINT: '/api/media/getUrlForFileUpload',
  APP_STRIPE_KEY: '',
  APP_GOOGLE_MAPS_API_KEY: '',
  APP_MUI_X_KEY: '',

  APP_GTM_ID: 'GTM-PG352QDF',
  APP_GTM_AUTH: '',
  APP_GTM_ENV: '',

  APP_POSTHOG_ENABLED: 'false',
  APP_POSTHOG_KEY: '',
  APP_KLAVIYO_ENABLED: 'false',
  APP_KLAVIYO_API_TOKEN: '',
  APP_PLAUSIBLE_ENABLED: 'false',
  APP_PLAUSIBLE_DATA_DOMAIN: 'ellipsis.sc',

  APP_ENABLE_SKIPPER_SELECT: '',
  APP_DISABLE_CALENDAR_SYNC: '',
  APP_FEATURE_READ_RECEIPTS_UI: 'true',
  APP_FEATURE_MODERATION_ORIGINAL_RESTORE: 'false',

  APP_OFFER_EXPIRY_THRESHOLD_DAYS: '30',
};

const merge = (...sources: Partial<AppRuntimeConfig>[]): AppRuntimeConfig => {
  const out: Record<string, string> = { ...defaults };
  for (const src of sources) {
    for (const [k, v] of Object.entries(src)) {
      if (v !== undefined && v !== null && v !== '') out[k] = String(v);
    }
  }
  return out as AppRuntimeConfig;
};

// SSR only: the same whitelisted keys `src/pages/+Head.tsx` injects into
// `window.__APP_CONFIG__`, read from the server's own environment. The keys
// of `defaults` are exactly the AppRuntimeConfig keys.
const fromServerEnv = (): Partial<AppRuntimeConfig> => {
  if (typeof process === 'undefined' || !process.env) return {};
  const out: Record<string, string> = {};
  for (const key of Object.keys(defaults)) {
    const value = process.env[key];
    if (value !== undefined) out[key] = value;
  }
  return out as Partial<AppRuntimeConfig>;
};

export const appConfig: AppRuntimeConfig = merge(
  typeof window === 'undefined' ? fromServerEnv() : fromWindow()
);

/**
 * Returns true if the given env string represents a truthy boolean flag.
 * Accepts "true", "1", "yes", "on" (case-insensitive). Anything else is
 * considered false, including empty strings.
 */
export const isFlagEnabled = (value: string | undefined): boolean => {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
};

// --- Google Tag Manager helpers ----------------------------------------

export const GTM_DATA_LAYER_NAME = 'PageDataLayer';

type GtmModuleArgs = {
  gtmId: string;
  auth?: string;
  preview?: string;
  dataLayer?: Record<string, unknown>;
  dataLayerName?: string;
};

export const gtmInitArgs = (
  extra: Omit<GtmModuleArgs, 'gtmId' | 'auth' | 'preview'> = {}
): GtmModuleArgs => {
  const args: GtmModuleArgs = {
    gtmId: appConfig.APP_GTM_ID,
    dataLayerName: GTM_DATA_LAYER_NAME,
    ...extra,
  };
  if (appConfig.APP_GTM_AUTH && appConfig.APP_GTM_ENV) {
    args.auth = appConfig.APP_GTM_AUTH;
    args.preview = appConfig.APP_GTM_ENV;
  }
  return args;
};
